import { createElement } from "react";

import toast from "react-hot-toast";

import * as Sentry from "@sentry/react";
import { hashKey } from "@tanstack/react-query";
import { useAuthStore } from "stores/auth";
import { useChallengeIdeaFiltersStore } from "stores/challengeIdeaFilters";
import { useMessagesStore } from "stores/messages";
import { resetSession, useSessionStore } from "stores/session";

import { API, APIError } from "simplydo/core/api";
import { WebsocketAPI } from "simplydo/core/api/websocket";
import { setOrgTimezone } from "simplydo/core/dates";
import {
  getLocalStorageItem,
  localStorageIsSupported,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import { i18n } from "simplydo/core/i18n";
import { asQuery, queryKey } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button } from "simplydo/react/ui/button";

export const websocketApi = new WebsocketAPI({
  websocketUrl: import.meta.env.VITE_WEBSOCKET_API_URL,
  generateToken: () =>
    new Promise((resolve) =>
      api.auth.requestWebsocketToken(
        ({ token: newWebsocketToken }) => resolve(newWebsocketToken),
        () => resolve(null),
      ),
    ),
});

// Cached queries are only cleared once a different user signs in; removing them at logout would make still-mounted pages refetch without a token
let sessionUserId: string | undefined;

const removeQueriesExceptCurrentUser = () => {
  const currentUserHash = hashKey(api.currentUserQueryKey());
  api.queryClient.removeQueries({
    predicate: (query) => query.queryHash !== currentUserHash,
  });
};

const onLogout = () => {
  // @ts-ignore
  window.$chatwoot?.reset();
  websocketApi.disconnect();
  api.token = null;
  api.revertToken = null;
  removeLocalStorageItem(api.REVERT_TOKEN);
  api.queryClient.removeQueries({
    queryKey: queryKey(api.messages.getThreads),
  });
  useMessagesStore.setState({ selectedMessageThreads: [] });
  api.clearCurrentUser();
  useAuthStore.getState().logout();
  resetSession();
};

const api = new API({
  getApiUrl: () => {
    return import.meta.env.VITE_API_URL;
  },
  onBeforeRequest: (xhr) => {
    const gitSha = import.meta.env.VITE_GIT_SHA;
    if (gitSha) {
      xhr.setRequestHeader("SimplyDo-ClientVersion", gitSha);
    }

    const now = new Date();
    const user = api.getCurrentUser();
    if (
      user?._id &&
      localStorageIsSupported() &&
      now.getTime() - new Date(getLocalStorageItem("heartbeat")!).getTime() >
        3600000
    ) {
      setLocalStorageItem("heartbeat", now.toString());
      api.users.heartbeat(
        user._id,
        () => {},
        () => {},
      );
    }
  },
  onLogin: (success: () => void, fail: (error: APIError) => void) => {
    if (!api.token) {
      const token = getLocalStorageItem("auth_token");
      if (token) {
        api.token = token;
      } else {
        fail?.({ status: 401, message: "No token found" });
        return;
      }
    }

    // Ghost token is never persisted; leftover revert_token after reload would fake an active ghost session
    if (!api.revertToken) {
      removeLocalStorageItem(api.REVERT_TOKEN);
    }

    useAuthStore.getState().beginSession();

    const params = new SearchParams(window.location.search);
    const utmReferrer =
      params.get("utm-link") || useSessionStore.getState().utmLink;

    // Wrap queryFn with timeout as safety measure
    // (authenticatedRequest now always calls callbacks, but timeout prevents infinite hangs)
    const queryFn = asQuery(api.users.me, [utmReferrer])!;
    const wrappedQueryFn = async () => {
      const timeoutPromise = new Promise<OpenAPI.GET<"/users/me">["response"]>(
        (_, reject) => {
          setTimeout(
            () =>
              reject(
                new Error(
                  "User query timed out - authentication may have failed",
                ),
              ),
            30000, // 30 second timeout
          );
        },
      );
      return Promise.race([queryFn(), timeoutPromise]);
    };
    api.queryClient
      .fetchQuery({
        queryKey: queryKey(api.users.me),
        queryFn: wrappedQueryFn,
        staleTime: 0,
      })
      .then((user) => {
        const me = user as UserMe;
        // /users/me has recorded the referral; App strips the URL and stored copies.
        useSessionStore.setState({ clearUtm: true });
        if (sessionUserId && sessionUserId !== me._id) {
          removeQueriesExceptCurrentUser();
        }
        sessionUserId = me._id;
        websocketApi.connect();
        useAuthStore.getState().finishSession();
        const ideaViewPreference = getLocalStorageItem(
          "ideaViewTypePreference",
        );
        if (ideaViewPreference && ideaViewPreference !== "board") {
          useChallengeIdeaFiltersStore
            .getState()
            .setIdeaViewType(ideaViewPreference as "card" | "list" | "board");
        }

        const searchParams = new SearchParams(window.location.search);
        const paramLang = searchParams.get("lang");
        if (paramLang) {
          i18n.changeLanguage(paramLang);
          if (me.language && me.language !== paramLang) {
            toast(
              (t) =>
                createElement("div", { style: { fontSize: "0.9em" } }, [
                  "You followed a link with a different language preference than yours.",
                  createElement(
                    "div",
                    {
                      style: {
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "1rem",
                      },
                    },
                    [
                      createElement(
                        Button,
                        {
                          size: "small",
                          onClick: () => {
                            i18n.changeLanguage(me.language);
                            toast.dismiss(t.id);
                          },
                        },
                        "Revert",
                      ),
                      createElement(
                        Button,
                        {
                          size: "small",
                          color: "primary",
                          onClick: () => {
                            toast.dismiss(t.id);
                          },
                        },
                        "Keep",
                      ),
                    ],
                  ),
                ]),
              { duration: Infinity },
            );
          }
        } else if (me.language) {
          i18n.changeLanguage(me.language);
        } else {
          i18n.changeLanguage(me.ownerOrganisation?.defaultLanguage || "en");
        }
        setOrgTimezone(me.ownerOrganisation?.defaultTimezone);
        // @ts-ignore - Seems not typed correctly in @sentry/react
        Sentry.setUser({
          id: me._id,
          username: me.profile.fullName,
          organisation: me.ownerOrganisation?.name,
          orgId: me.ownerOrganisation?._id,
        });
        success?.();
      })
      .catch((err) => {
        fail?.(err);
        useAuthStore.getState().getUserFail(err);
      });
  },
  onLogout: onLogout,
  onFailedRequest: (xhr, onFail) => {
    if (xhr.status === 530) {
      onFail?.({
        status: 530,
        message: "Access from this IP address is not allowed",
      });
      return;
    } else if (xhr.status === 401) {
      onLogout();
      onFail?.({ status: 401, message: "Session expired" });
      return;
    }
    try {
      const response = JSON.parse(xhr.responseText);
      onFail?.({
        ...response,
        status: xhr.status,
        message: response.message || xhr.statusText,
      });
    } catch {
      onFail?.({ status: xhr.status, message: xhr.statusText });
    }
  },
});

// Register query client globally to enable access to the React Query devtools
// @ts-ignore - React Query devtools
// window.__TANSTACK_QUERY_CLIENT__ = api.queryClient;

window.api = api;

export default api;
